import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  createstructure,
  deletestructure,
  liststaff,
  liststructure,
} from "../../../services/admin";

// Per-staff salary structure: the standard monthly pay used as the template
// for bulk payslip generation. Same components as the payslip.
const EARNINGS = [
  ["basic_salary", "Basic Salary"],
  ["hra", "HRA"],
  ["transport_allowance", "Transport Allowance"],
  ["medical_allowance", "Medical Allowance"],
  ["other_allowances", "Other Allowances"],
];
const DEDUCTIONS = [
  ["provident_fund", "Provident Fund (PF)"],
  ["tax_deduction", "Tax Deduction"],
  ["other_deductions", "Other Deductions"],
];

const emptyForm = {
  id: "",
  staff_id: "",
  basic_salary: "",
  hra: "",
  transport_allowance: "",
  medical_allowance: "",
  other_allowances: "",
  provident_fund: "",
  tax_deduction: "",
  other_deductions: "",
  remarks: "",
};

export default function SalaryStructure() {
  const [staffList, setStaffList] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);

  const branch = localStorage.getItem("branch");

  function loadStaff() {
    liststaff(branch).then((res) => {
      if (res?.status === true) {
        setStaffList(
          res.data.details.map((s) => ({
            value: s.id,
            label: `${s.staff_name} (${s.staff_code})`,
          }))
        );
      }
    });
  }

  function loadStructures() {
    liststructure(branch).then((res) => {
      if (res?.status === true) {
        setRows(res.data?.details || []);
      }
    });
  }

  useEffect(() => {
    loadStaff();
    loadStructures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const num = (v) => parseFloat(v) || 0;
  const gross = EARNINGS.reduce((sum, [k]) => sum + num(form[k]), 0);
  const totalDeductions = DEDUCTIONS.reduce((sum, [k]) => sum + num(form[k]), 0);
  const net = gross - totalDeductions;

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function resetForm() {
    setForm(emptyForm);
    setSubmitted(false);
  }

  function editRow(row) {
    setForm({
      id: row.id,
      staff_id: row.staff_id,
      basic_salary: row.basic_salary,
      hra: row.hra,
      transport_allowance: row.transport_allowance,
      medical_allowance: row.medical_allowance,
      other_allowances: row.other_allowances,
      provident_fund: row.provident_fund,
      tax_deduction: row.tax_deduction,
      other_deductions: row.other_deductions,
      remarks: row.remarks || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleDelete(id) {
    Swal.fire({
      text: "Delete this salary structure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ac0404",
      confirmButtonText: "Delete",
    }).then((r) => {
      if (r.isConfirmed) {
        deletestructure(id).then((res) => {
          if (res?.status === true) {
            toast.success(res.message);
            loadStructures();
          }
        });
      }
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
    if (!form.staff_id) {
      toast.error("Please select a staff member");
      return;
    }
    const temp = new FormData();
    Object.entries(form).forEach(([k, v]) => temp.append(k, v ?? ""));
    createstructure(temp).then((res) => {
      if (res?.status === true) {
        toast.success(res.message);
        resetForm();
        loadStructures();
      } else if (res?.message) {
        toast.error(res.message);
      }
    });
  }

  const inputCol = (key, label) => (
    <div className="col-xl-3 col-sm-6" key={key}>
      <div className="mb-3">
        <label className="form-label">{label}</label>
        <input
          type="number"
          min="0"
          className="form-control"
          placeholder={label}
          value={form[key]}
          onChange={(e) => setField(key, e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2 className="main-content-title tx-24 mg-b-5">Salary Structure</h2>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a>Payslip</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Salary Structure
          </li>
        </ol>
      </div>

      <div className="content-body">
        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header">
                <h2 className="main-content-title tx-24 mg-b-5">
                  {form.id ? "Edit Salary Structure" : "Add Salary Structure"}
                </h2>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-xl-4 col-sm-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Staff <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={staffList}
                        value={
                          staffList.find((s) => s.value === form.staff_id) || null
                        }
                        onChange={(opt) => setField("staff_id", opt ? opt.value : "")}
                        placeholder="Select Staff"
                        isSearchable
                        isDisabled={!!form.id}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                      />
                      {submitted && !form.staff_id && (
                        <span className="text-danger">Staff is required</span>
                      )}
                    </div>
                  </div>
                </div>

                <h6 className="mt-2 mb-3 fw-bold">Earnings</h6>
                <div className="row">{EARNINGS.map(([k, l]) => inputCol(k, l))}</div>

                <h6 className="mt-2 mb-3 fw-bold">Deductions</h6>
                <div className="row">{DEDUCTIONS.map(([k, l]) => inputCol(k, l))}</div>

                <div className="row">
                  <div className="col-xl-4 col-sm-6">
                    <div className="mb-3">
                      <label className="form-label">Gross Salary</label>
                      <input className="form-control" value={gross} readOnly />
                    </div>
                  </div>
                  <div className="col-xl-4 col-sm-6">
                    <div className="mb-3">
                      <label className="form-label">Total Deductions</label>
                      <input className="form-control" value={totalDeductions} readOnly />
                    </div>
                  </div>
                  <div className="col-xl-4 col-sm-6">
                    <div className="mb-3">
                      <label className="form-label">Net Salary</label>
                      <input className="form-control fw-bold" value={net} readOnly />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="mb-3">
                      <label className="form-label">Remarks</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Remarks"
                        value={form.remarks}
                        onChange={(e) => setField("remarks", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="text-end">
                  {form.id && (
                    <a className="btn btn-secondary me-2" onClick={resetForm}>
                      Cancel
                    </a>
                  )}
                  <a className="btn btn-success" onClick={handleSubmit}>
                    {form.id ? "Update" : "Save"}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header">
                <h2 className="main-content-title tx-24 mg-b-5">Salary Structures</h2>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Staff</th>
                        <th>Gross</th>
                        <th>Deductions</th>
                        <th>Net</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            No salary structures yet.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.id}>
                            <td>
                              {row.staff_name} ({row.staff_code})
                            </td>
                            <td>{row.gross_salary}</td>
                            <td>{row.total_deductions}</td>
                            <td>{row.net_salary}</td>
                            <td>
                              <a
                                className="btn-sm btn-info me-2"
                                onClick={() => editRow(row)}
                              >
                                Edit
                              </a>
                              <a
                                className="btn-sm btn-danger"
                                onClick={() => handleDelete(row.id)}
                              >
                                Delete
                              </a>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
